import {
	ReactElement,
	RefObject,
	useContext,
	useCallback,
	useRef,
	useState,
	Fragment,
	KeyboardEvent,
	useEffect,
	useMemo
} from "react";
import {
	KeyBoardListNavigationContext,
	KeyboardListNavigationItemOptions,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem,
	useResizeObserver,
} from "client/Hooks";
import { DropOverPreview } from "../DropOverPreview/DropOverPreview";
import { ActionListItem } from "../ActionList/ActionListItem";
import styles from "./Select.module.less";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNil,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	combineAttachmentKinds,
	ControlAttachmentContext,
	ControlAttachmentOptions,
	NO_CONTROL_ATTACHMENTS
} from "../ControlAttachment";
import { DropOver } from "../DropOver/DropOver";
import { ActionListBox } from "../ActionListBox/ActionListBox";
import { ActionList } from "../ActionList/ActionList";
import { localize } from "client_legacy/ndaw/culture";
import { ActionListDivider } from "../ActionList/ActionListDivider";
import {
	Icon,
	IconValue
} from "../Icon/Icon";
import { InputLike } from "../InputLike/InputLike";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { Key } from "client/Misc/commonTypes";
import { ActionListCollapsibleSection } from "../ActionList/ActionListCollapsibleSection";
import { SearchInputForm, SearchInputFormHandle } from "../SearchInputForm/SearchInputForm";
import { contains, normalizeForSearch } from "client/Data/searching";
import { NoResults } from "../NoResults/NoResults";
import { SearchPlaceholder } from "../SearchInput/SearchPlaceholder";
import { KeyEventArguments } from "../TextInput/TextInput";

export type SelectOption<TValue> = {
	text: string;
	value: TValue;
	groupName?: string;
	valueTrackingId?: string;
	icon?: IconValue;
	isDisabled?: boolean;
};

export type SelectBranch<TValue> = {
	text: string;
	uniqueId: Guid;
	groupName?: string;
	icon?: IconValue;
	isDisabled?: boolean;
	children: readonly SelectItem<TValue>[];
};

export type SelectItem<TValue> =
	| SelectBranch<TValue>
	| SelectOption<TValue>;

export type SelectProps<TValue> = {
	value: TValue | nil;
	onChange(value: TValue): void;
	options: readonly SelectItem<TValue>[];
	hasBusyIndicator?: boolean;
	isInvalid?: boolean;
	isDisabled?: boolean;
	trackingId?: string;
	attachedOn?: ControlAttachmentOptions;
	placeholder?: string;
	areEqual?(a: TValue | nil, b: TValue): boolean;
	menuTitle: string;
	height?: "default" | "large";
	tooltip?: string;
} & ({
	doesSupportFiltering?: false;
} | {
	doesSupportFiltering: true;
	placeholderPropertyNames: readonly string[];
});

export const Select = <TValue, >(props: SelectProps<TValue>): ReactElement => {
	const {
		value,
		onChange,
		options,
		hasBusyIndicator = false,
		isInvalid = false,
		isDisabled = false,
		attachedOn = [],
		trackingId,
		placeholder = "",
		areEqual = defaultAreEqual,
		menuTitle,
		height = "default",
		tooltip,
		doesSupportFiltering
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [dropOverId] = useState<Guid>(() => newGuid());
	const [collapsedItemIds, setCollapsedItemIds] = useState<Set<Guid>>(new Set());
	const [searchTerm, setSearchTerm] = useState<string>("");

	const previewRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const searchRef: RefObject<SearchInputFormHandle | null> = useRef<SearchInputFormHandle>(null);
	const { width } = useResizeObserver(ref);

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm),
		[searchTerm]
	);

	const flattenItemsIdendifiers = useMemo(
		(): readonly ItemIdentifier<TValue>[] => {
			const flattenItems: ItemIdentifier<TValue>[] = [];
			getFlattenItemsIdentifiers(options, flattenItems);
			return flattenItems;
		},
		[options]
	);

	const filteredItems = useMemo(
		(): readonly SelectItem<TValue>[] => getFilteredItems(options, normalizedSearchTerm),
		[options, normalizedSearchTerm]
	);

	const focusPreviewHandler = useCallback(
		(): void => {
			previewRef.current
				?.focus();
		},
		[]
	);

	const cancelHandler = useCallback(
		(): void => {
			setIsOpen(false);
			focusPreviewHandler();
		},
		[focusPreviewHandler]
	);

	const {
		contextData,
		focusFirstItemHandler,
		focusItemAtIndexHandler
	} = useKeyboardListNavigation({
		onCancel: cancelHandler,
		shouldCancelDirectlyFromItems: true,
		onFocusInput: () => {
			searchRef
				.current
				?.focusInput();
		}
	});

	const {
		attachedOn: attachedOnFromContext
	} = useContext(ControlAttachmentContext);

	const { id: dropOverPreviewId } = useContext(UniqueInputIdContext);

	const toggleCollapseHandler = useCallback(
		(item: SelectBranch<TValue>): void => {
			setCollapsedItemIds(collapsedIds => {
				const newSet: Set<Guid> = new Set(collapsedIds);

				if (!newSet.delete(item.uniqueId)) {
					newSet.add(item.uniqueId);
				}

				return newSet;
			});
		},
		[]
	);

	const clickHandler = useCallback(
		(item: SelectOption<TValue>): void => {
			setIsOpen(false);
			onChange(item.value);
		},
		[onChange]
	);

	const selectedOption = useMemo(
		(): SelectOption<TValue> | null => {
			const result: TryResult<SelectOption<TValue>> = tryFindOption(
				value,
				options,
				areEqual
			);

			return result.wasSuccessful
				? result.result
				: null;
		},
		[
			value,
			options,
			areEqual
		]
	);

	const hasPlaceholder: boolean = isNil(selectedOption);

	const previewText: string = hasPlaceholder
		? placeholder
		: selectedOption?.text ?? "";

	const getItemGlobalIndexHandler = useCallback(
		(item: SelectItem<TValue>): number => findItemGlobalIndex(
			item,
			flattenItemsIdendifiers,
			areEqual
		),
		[flattenItemsIdendifiers, areEqual]
	);

	const previewKeyDownHandler = useCallback(
		(e: KeyboardEvent): void => {
			switch (e.key) {
				case Key.ArrowDown:
				case Key.Enter:
				case Key.Space:
					e.preventDefault();
					setIsOpen(true);

					if (hasBusyIndicator) {
						return;
					}

					// Ensure the dropdown is mounted before focusing an item
					setTimeout(
						(): void => {
							if (searchRef.current) {
								searchRef
									.current
									.focusInput();

								return;
							}

							if (selectedOption) {
								focusItemAtIndexHandler(getItemGlobalIndexHandler(selectedOption));
							} else {
								focusFirstItemHandler();
							}
						},
						1
					);
					return;
				case Key.Escape:
				case Key.Tab:
					setIsOpen(false);
					return;
				default:
					return;
			}
		},
		[
			hasBusyIndicator,
			selectedOption,
			focusItemAtIndexHandler,
			focusFirstItemHandler,
			getItemGlobalIndexHandler
		]
	);

	const listSearchKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.Escape:
				case Key.Tab:
					cancelHandler();
					return true;
				default:
					return false;
			}
		},
		[cancelHandler]
	);

	useEffect(
		(): void => {
			if (!isOpen) {
				setCollapsedItemIds(new Set());
				setSearchTerm("");
			}
		},
		[isOpen]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setCollapsedItemIds(new Set());
		},
		[searchTerm]
	);

	const renderContent = (): ReactElement => {
		const itemsCount: number = filteredItems.length;

		if (itemsCount === 0 && !hasBusyIndicator && !searchTerm) {
			return <EmptyValuePlaceholder />;
		}

		return (
			<div className={styles.content}>
				{doesSupportFiltering && (
					<div className={styles.searchHeader}>
						<SearchInputForm
							ref={searchRef}
							trackingId={generateTrackingId(trackingId, "__search")}
							onNavigateToResults={focusFirstItemHandler}
							onChange={setSearchTerm}
							icon="filter"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={props.placeholderPropertyNames}
								/>
							)}
							onKeyDown={listSearchKeyDownHandler}
							hasMarginBottom={false}
						/>
					</div>
				)}
				<div className={styles.list}>
					<ActionList
						width="fill"
						trackingId={generateTrackingId(trackingId, "__dropover")}
					>
						{searchTerm && itemsCount === 0
							? (
								<NoResults
									icon="magnifying-glass"
									title={localize("_NoSearchResults")}
									message={localize("_PleaseTrySearchingWithAnotherTerm")}
									trackingId="no-search-results"
								/>
							)
							: (
								<KeyBoardListNavigationContext.Provider
									value={contextData}
								>
									<List
										items={filteredItems}
										value={value}
										getItemGlobalIndex={getItemGlobalIndexHandler}
										areEqual={areEqual}
										onSelect={clickHandler}
										onFocusPreview={focusPreviewHandler}
										trackingId={trackingId}
										indentionLevel={0}
										collapsedItemIds={collapsedItemIds}
										onToggleCollapse={toggleCollapseHandler}
									/>
								</KeyBoardListNavigationContext.Provider>
							)
						}
					</ActionList>
				</div>
			</div>
		);
	};

	return (
		<ControlAttachmentContext.Provider value={NO_CONTROL_ATTACHMENTS}>
			<div
				{...setTrackingId(trackingId)}
				ref={ref}
			>
				<DropOverPreview
					ref={previewRef}
					isOpen={isOpen}
					onClick={() => setIsOpen(x => !x)}
					isInvalid={isInvalid}
					isDisabled={isDisabled}
					isPlaceholder={hasPlaceholder}
					attachedOn={combineAttachmentKinds(attachedOn, attachedOnFromContext)}
					height={height}
					tooltip={tooltip}
					id={dropOverPreviewId}
					onKeyDown={previewKeyDownHandler}
					ariaHaspopup="listbox"
					ariaControls={dropOverId}
					ariaLabel={menuTitle}
				>
					<div className={styles.preview}>
						{selectedOption?.icon && (
							<Icon value={selectedOption.icon} />
						)}
						<div className={styles.text}>
							{previewText}
						</div>
					</div>
				</DropOverPreview>
				{isOpen && ref.current && (
					<DropOver
						target={ref.current}
						onClose={() => setIsOpen(false)}
						role="listbox"
						id={dropOverId}
						ariaLabel={menuTitle}
					>
						<ActionListBox
							width={width}
							isHeightConstrainted
							hasBusyIndicator={hasBusyIndicator}
							menuTitle={menuTitle}
							responsiveMode={{
								kind: "auto",
								height: doesSupportFiltering
									? "fill"
									: undefined
							}}
							trackingId={generateTrackingId(trackingId, "__dropover__wrapper")}
						>
							{renderContent()}
						</ActionListBox>
					</DropOver>
				)}
			</div>
		</ControlAttachmentContext.Provider>
	);
};

Select.displayName = "Select";

type ItemIdentifier<TValue> = {
	kind: "branch";
	uniqueId: Guid;
} | {
	kind: "option";
	value: TValue;
};

type ListProps<TValue> = {
	value: TValue | nil;
	items: readonly SelectItem<TValue>[];
	indentionLevel: number;
	trackingId?: string;
	getItemGlobalIndex: (item: SelectItem<TValue>) => number;
	areEqual: (a: TValue | nil, b: TValue) => boolean;
	onSelect: (item: SelectOption<TValue>) => void;
	onFocusPreview: () => void;
	collapsedItemIds: ReadonlySet<Guid>;
	onToggleCollapse: (item: SelectBranch<TValue>) => void;
};

const List = <TValue, >(props: ListProps<TValue>): ReactElement => {
	const {
		value,
		items,
		indentionLevel,
		trackingId,
		getItemGlobalIndex,
		areEqual,
		onSelect,
		onFocusPreview,
		collapsedItemIds,
		onToggleCollapse
	} = props;

	let lastGroupName: string | undefined = undefined;

	return (
		<>
			{items.map(
				(item: SelectItem<TValue>, index) => {
					const { groupName } = item;

					let hasDivider: boolean = false;

					if (groupName !== lastGroupName) {
						if (index > 0) {
							hasDivider = true;
						}

						lastGroupName = groupName;
					}

					return (
						<Fragment key={index}>
							{hasDivider && <ActionListDivider />}
							<ListItem
								item={item}
								value={value}
								trackingId={trackingId}
								indentionLevel={indentionLevel}
								getItemGlobalIndex={getItemGlobalIndex}
								areEqual={areEqual}
								onSelect={onSelect}
								onFocusPreview={onFocusPreview}
								collapsedItemIds={collapsedItemIds}
								onToggleCollapse={onToggleCollapse}
							/>
						</Fragment>
					);
				}
			)}
		</>
	);
};
List.displayName = "List";

type ListItemProps<TValue> = {
	item: SelectItem<TValue>;
	value: TValue | nil;
	trackingId?: string;
	indentionLevel: number;
	getItemGlobalIndex: (item: SelectItem<TValue>) => number;
	onSelect: (item: SelectOption<TValue>) => void;
	areEqual: (a: TValue | nil, b: TValue) => boolean;
	onFocusPreview: () => void;
	collapsedItemIds: ReadonlySet<Guid>;
	onToggleCollapse: (item: SelectBranch<TValue>) => void;
};

const ListItem = <TValue,>(props: ListItemProps<TValue>): ReactElement => {
	const {
		item,
		value,
		trackingId,
		indentionLevel,
		getItemGlobalIndex,
		areEqual,
		onSelect,
		onFocusPreview,
		collapsedItemIds,
		onToggleCollapse
	} = props;

	const {
		isDisabled,
		text,
		icon
	} = item;

	const index = useMemo(
		(): number => getItemGlobalIndex(item),
		[
			item,
			getItemGlobalIndex
		]
	);

	const selectHandler = useCallback(
		(item: SelectOption<TValue>): void => {
			if (item.isDisabled) {
				return;
			}

			onFocusPreview();
			onSelect(item);
		},
		[onSelect, onFocusPreview]
	);

	const keyDownHandler = useCallback(
		(key: Key): void => {
			switch (key) {
				case Key.Enter:
					if (isOption(item)) {
						selectHandler(item);
					} else {
						onToggleCollapse(item);
					}
					break;
				case Key.Space:
					if (isBranchItem(item)) {
						onToggleCollapse(item);
					} else {
						selectHandler(item);
					}
					break;
				default:
					// Nothing to do
			}
		},
		[
			item,
			onToggleCollapse,
			selectHandler
		]
	);

	const options = useMemo(
		(): KeyboardListNavigationItemOptions => ({
			itemIndex: index,
			executeKey: [
				Key.Enter,
				Key.Space
			],
			shouldCancelOnTab: true,
			onExecute: keyDownHandler
		}),
		[index, keyDownHandler]
	);

	const hasSelectedItemIndicator = useMemo(
		(): boolean => {
			if (!isBranchItem(item) || !collapsedItemIds.has(item.uniqueId)) {
				return false;
			}

			const result: TryResult<SelectOption<TValue>> = tryFindOption(
				value,
				item.children,
				areEqual
			);

			return result.wasSuccessful;
		},
		[
			item,
			collapsedItemIds,
			value,
			areEqual
		]
	);

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem(options);


	if (isOption(item)) {
		const isSelected: boolean = areEqual(value, item.value);

		return (
			<ActionListItem
				ref={ref}
				text={text}
				icon={icon}
				indentionLevel={indentionLevel}
				isDisabled={isDisabled}
				isActive={isSelected}
				onClick={() => selectHandler(item)}
				onKeyDown={onKeyDown}
				tabIndex={-1}
				role="option"
				trackingId={generateTrackingId(trackingId, `__item|${getItemValueTrackingId(item)}`)}
				rightIcon={isSelected
					? "check"
					: undefined
				}
			/>
		);
	}

	const isCollapsed: boolean = collapsedItemIds.has(item.uniqueId);

	return (
		<ActionListCollapsibleSection
			ref={ref}
			header={item.text}
			suffix={
				hasSelectedItemIndicator
					? "(1)"
					: undefined
			}
			trackingId={generateTrackingId(trackingId ?? "select-list", `__item|${item.uniqueId}`)}
			indentionLevel={indentionLevel}
			isCollapsed={isCollapsed}
			onToggle={() => onToggleCollapse(item)}
			onKeyDown={onKeyDown}
			tabIndex={-1}
			role="group"
			hasLine
		>
			<List
				items={item.children}
				value={value}
				getItemGlobalIndex={getItemGlobalIndex}
				areEqual={areEqual}
				onSelect={onSelect}
				onFocusPreview={onFocusPreview}
				trackingId={trackingId}
				indentionLevel={indentionLevel + 1}
				collapsedItemIds={collapsedItemIds}
				onToggleCollapse={onToggleCollapse}
			/>
		</ActionListCollapsibleSection>
	);
};
ListItem.displayName = "ListItem";

export const EmptyValuePlaceholder = () => {
	return (
		<div className={styles.emptyDataContainer}>
			<NoResults
				icon="list"
				title={localize("_NoItemsYet")}
				message={localize("_ThereAreNoItemsYet")}
				trackingId="no-items"
			/>
		</div>
	);
};

export type SelectReadOnlyProps<TValue> = {
	value: TValue | nil;
	options: readonly SelectItem<TValue>[];
	trackingId: string;
	areEqual?(a: TValue | nil, b: TValue): boolean;
};

export const SelectReadOnly = <TValue, >(props: SelectReadOnlyProps<TValue>): ReactElement => {
	const {
		value,
		options,
		trackingId,
		areEqual = (a, b) => a === b,
	} = props;

	const { id } = useContext(UniqueInputIdContext);

	const selectedOption = useMemo(
		(): SelectOption<TValue> | null => {
			const result: TryResult<SelectOption<TValue>> = tryFindOption(
				value,
				options,
				areEqual
			);

			return result.wasSuccessful
				? result.result
				: null;
		},
		[
			value,
			options,
			areEqual
		]
	);

	const renderContent = (): ReactElement => (
		<span className={styles.readOnlyContent}>
			{selectedOption?.icon && (
				<Icon value={selectedOption.icon} />
			)}
			<span className={styles.readOnlyContentText}>
				{selectedOption?.text ?? ""}
			</span>
		</span>
	);

	return (
		<>
			<InputLike
				isBorderless
				hasNoPadding
				value={renderContent()}
				trackingId={trackingId}
			/>
			<input
				id={id}
				type="button"
				disabled
				className={styles.readonlyHiddenInput}
				value={selectedOption?.text ?? ""}
			/>
		</>
	);
};
SelectReadOnly.displayName = "SelectReadOnly";

function defaultAreEqual<TValue>(a: TValue | nil, b: TValue): boolean {
	return a === b;
}

function isOption<TValue>(item: SelectItem<TValue>): item is SelectOption<TValue> {
	return !isBranchItem(item);
}

function isBranchItem<TValue>(item: SelectItem<TValue>): item is SelectBranch<TValue> {
	return isNotNil((item as SelectBranch<TValue>).children);
}

function tryFindOption<TValue>(
	value: TValue | nil,
	items: readonly SelectItem<TValue>[],
	areEqual: (a: TValue | nil, b: TValue) => boolean
): TryResult<SelectOption<TValue>> {
	for (const item of items) {
		if (isOption(item)) {
			if (areEqual(value, item.value)) {
				return { wasSuccessful: true, result: item };
			}
		} else {
			const result: TryResult<SelectOption<TValue>> = tryFindOption(
				value,
				item.children,
				areEqual
			);

			if (result.wasSuccessful) {
				return result;
			}
		}
	}

	return { wasSuccessful: false };
}

function getItemValueTrackingId<TValue>(item: SelectOption<TValue>): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return item.valueTrackingId;
	}

	return convertArbitraryValueForTrackingId(item.value);
}

function getFlattenItemsIdentifiers<TValue>(
	items: readonly SelectItem<TValue>[],
	result: ItemIdentifier<TValue>[]
): void {
	for (const item of items) {
		result.push(isOption(item)
			? {
				kind: "option",
				value: item.value
			}
			: {
				kind: "branch",
				uniqueId: item.uniqueId
			}
		);

		if (isBranchItem(item)) {
			getFlattenItemsIdentifiers(item.children, result);
		}
	}
}

function findItemGlobalIndex<TValue>(
	item: SelectItem<TValue>,
	flattenItemsIdendifiers: readonly ItemIdentifier<TValue>[],
	areEqual: (a: TValue, b: TValue) => boolean
): number {
	const index: number = flattenItemsIdendifiers
		.findIndex(
			x => isOption(item)
				? x.kind === "option" && areEqual(item.value, x.value)
				: x.kind === "branch" && (x.uniqueId === item.uniqueId)
		);

	if (index === -1) {
		throw new Error("Couldn't find item global index");
	}

	return index;
}

function getFilteredItems<TValue>(
	items: readonly SelectItem<TValue>[],
	searchTerm: string,
): readonly SelectItem<TValue>[] {
	const result: SelectItem<TValue>[] = [];

	for (const item of items) {
		if (isOption(item) && contains(item.text, searchTerm)) {
			result.push(item);
		}

		if (isBranchItem(item)) {
			const children: readonly SelectItem<TValue>[] = getFilteredItems(item.children, searchTerm);

			if (children.length > 0) {
				result.push({
					...item,
					children
				});
			}
		}
	}

	return result;
}